import React, { useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Container, Box, Typography, TextField, Button, Paper,
  Alert, CircularProgress, LinearProgress, List, ListItem, ListItemIcon, ListItemText
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import WarningIcon from '@mui/icons-material/Warning';
import { motion } from 'framer-motion';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { calculatePlanetaryPositions, calculateAscendant } from '../utils/ephemeris';
import { lookupCity, geocodeCity, getCityList, getUTCOffset } from '../utils/location';
import { registerUser } from '../services/transitCalendarService';
import { validatePassword, getStrengthColor, validateEmail, sanitizeInput, validateCoordinates } from '../utils/passwordValidation';
import { trafficSource } from '../utils/trafficSource';
import { sendSignupConversion } from '../utils/adsConversion';

const TransitCalendarRegister = () => {
  const navigate = useNavigate();
  // Birth details carried over from the free birth chart (FreeBirthChartPage), so they aren't typed twice
  const fromChart = useLocation().state?.fromChart || null;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Form fields
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [birthDate, setBirthDate] = useState(fromChart?.birthTime ? new Date(fromChart.birthTime) : new Date('1990-01-01T12:00:00'));
  const [cityName, setCityName] = useState(fromChart?.cityName || '');
  const [latitude, setLatitude] = useState(fromChart?.latitude || 0);
  const [longitude, setLongitude] = useState(fromChart?.longitude || 0);
  const [timezone, setTimezone] = useState(fromChart?.timezone || 'UTC');
  const [passwordValidation, setPasswordValidation] = useState(null);
  const [showPasswordRequirements, setShowPasswordRequirements] = useState(false);

  // Handle city name change and auto-populate coordinates. A slower answer for what was typed earlier must not overwrite the place
  // ("Dubli" arriving after "Dublin" put a Dublin birth in India, seen 2026-10-31)
  const cityLookup = useRef(0);
  const handleCityChange = async (newCityName) => {
    const lookup = ++cityLookup.current;
    setCityName(newCityName);
    const locationData = lookupCity(newCityName);
    if (locationData) {
      setLatitude(locationData.latitude);
      setLongitude(locationData.longitude);
      setTimezone(locationData.timezone);
      return;
    }
    // Fallback to geocoding API
    if (newCityName.trim().length >= 3) {
      const results = await geocodeCity(newCityName);
      if (lookup !== cityLookup.current) return;
      if (results.length > 0) {
        setLatitude(results[0].latitude);
        setLongitude(results[0].longitude);
        setTimezone(results[0].timezone);
      }
    }
  };

  // Handle password change with validation
  const handlePasswordChange = (newPassword) => {
    setPassword(newPassword);
    const validation = validatePassword(newPassword);
    setPasswordValidation(validation);
  };

  const handleRegister = async (e) => {
    e.preventDefault();

    // Sanitize inputs
    const sanitizedName = sanitizeInput(name);
    const sanitizedEmail = sanitizeInput(email);
    const sanitizedCity = sanitizeInput(cityName);

    // Validation
    if (!sanitizedName || !sanitizedEmail || !password) {
      setError('Please fill in all required fields');
      return;
    }

    // Email validation
    if (!validateEmail(sanitizedEmail)) {
      setError('Please enter a valid email address');
      return;
    }

    // Password validation
    const passwordCheck = validatePassword(password);
    if (!passwordCheck.isValid) {
      setError(`Password requirements not met: ${passwordCheck.errors.join(', ')}`);
      return;
    }

    // Coordinate validation
    const coordCheck = validateCoordinates(latitude, longitude);
    if (!coordCheck.isValid) {
      setError(coordCheck.error);
      return;
    }

    if (!sanitizedCity || latitude === 0) {
      setError('Please select a valid birth location');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Calculate timezone offset
      const utcOffset = getUTCOffset(birthDate, timezone);

      // Convert birth date to UTC
      const year = birthDate.getFullYear();
      const month = birthDate.getMonth();
      const day = birthDate.getDate();
      const hours = birthDate.getHours();
      const minutes = birthDate.getMinutes();
      const seconds = birthDate.getSeconds();
      const birthDateUTC = new Date(Date.UTC(year, month, day, hours, minutes, seconds) - utcOffset * 60 * 60 * 1000);

      // Calculate natal positions
      const natalPlanets = calculatePlanetaryPositions(birthDateUTC);
      const ascendantLongitude = calculateAscendant(birthDateUTC, latitude, longitude);

      // Format natal data for storage
      const natalPositions = {};
      natalPlanets.forEach(planet => {
        natalPositions[planet.name] = {
          sign: planet.sign,
          degree: planet.degreeInSign,
          longitude: planet.longitude,
          isRetrograde: planet.isRetrograde
        };
      });

      // Calculate ascendant sign
      const ZODIAC_SIGNS = ['Aries', 'Taurus', 'Gemini', 'Cancer', 'Leo', 'Virgo',
                           'Libra', 'Scorpio', 'Sagittarius', 'Capricorn', 'Aquarius', 'Pisces'];
      const ascendantSignIndex = Math.floor(ascendantLongitude / 30);
      const ascendantSign = ZODIAC_SIGNS[ascendantSignIndex];

      // Register user with real API (using sanitized inputs)
      const result = await registerUser({
        name: sanitizedName,
        email: sanitizedEmail,
        password,
        // Persist the TRUE UTC birth instant (wall-clock minus birth-location
        // offset), not the picker Date's device-dependent toISOString()
        birthDate: birthDateUTC.toISOString(),
        birthTime: birthDate.toTimeString().split(' ')[0],
        birthLocation: sanitizedCity,
        birthLat: latitude,
        birthLon: longitude,
        timezone,
        natalPositions,
        source: trafficSource()
      });

      // Store authentication token
      if (result.userId && result.email) {
        localStorage.setItem('transit-calendar-token', 'registered');
        localStorage.setItem('transit-calendar-email', result.email);
        sendSignupConversion();

        // Redirect to login page
        navigate('/transit-calendar/login', {
          state: { message: 'Registration successful! Please log in.' }
        });
      }

    } catch (err) {
      setError(err.message || 'Registration failed. Please try again.');
      console.error('Registration error:', err);
      setLoading(false);
    }
  };

  return (
    <Box sx={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      py: { xs: 4, sm: 8 },
      px: { xs: 2, sm: 0 }
    }}>
      <Container maxWidth="md">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <Paper sx={{ p: { xs: 3, sm: 4 } }}>
            <Typography
              variant="h3"
              gutterBottom
              sx={{
                fontWeight: 700,
                textAlign: 'center',
                mb: 3,
                fontSize: { xs: '1.75rem', sm: '2.5rem', md: '3rem' }
              }}
            >
              Create Your Transit Calendar Account
            </Typography>

            <Typography
              variant="body1"
              sx={{
                mb: 4,
                textAlign: 'center',
                color: 'text.secondary',
                fontSize: { xs: '0.875rem', sm: '1rem' }
              }}
            >
              Get personalized weekly transit predictions based on your natal chart
            </Typography>

            {fromChart && (
              <Alert severity="info" sx={{ mb: 3 }}>
                {fromChart.cityName
                  ? 'Your birth details are filled in from your chart. Please check them, then add your name, email and a password.'
                  : 'Your birth date and time are filled in from your chart. Please check them and add your birth location, name, email and a password.'}
              </Alert>
            )}

            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}

            <form onSubmit={handleRegister}>
              {/* Name */}
              <TextField
                fullWidth
                label="Full Name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                sx={{ mb: 3 }}
              />

              {/* Email */}
              <TextField
                fullWidth
                type="email"
                label="Email Address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                sx={{ mb: 3 }}
                helperText="You'll use this to log in"
              />

              {/* Password */}
              <TextField
                fullWidth
                type="password"
                label="Password"
                value={password}
                onChange={(e) => handlePasswordChange(e.target.value)}
                onFocus={() => setShowPasswordRequirements(true)}
                required
                sx={{ mb: 1 }}
                helperText="Create a strong password with at least 12 characters"
              />

              {/* Password Strength Indicator */}
              {password && passwordValidation && (
                <Box sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Typography variant="caption" sx={{ fontWeight: 600 }}>
                      Password Strength: {passwordValidation.strength.toUpperCase()}
                    </Typography>
                    <Box sx={{ flexGrow: 1 }}>
                      <LinearProgress
                        variant="determinate"
                        value={passwordValidation.score}
                        sx={{
                          height: 8,
                          borderRadius: 4,
                          bgcolor: 'grey.200',
                          '& .MuiLinearProgress-bar': {
                            bgcolor: getStrengthColor(passwordValidation.strength),
                            borderRadius: 4
                          }
                        }}
                      />
                    </Box>
                  </Box>

                  {/* Password Requirements */}
                  {showPasswordRequirements && (
                    <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                      <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 1 }}>
                        Password Requirements:
                      </Typography>
                      <List dense>
                        <ListItem sx={{ py: 0 }}>
                          <ListItemIcon sx={{ minWidth: 32 }}>
                            {password.length >= 12 ? (
                              <CheckCircleIcon sx={{ color: 'success.main', fontSize: 18 }} />
                            ) : (
                              <CancelIcon sx={{ color: 'error.main', fontSize: 18 }} />
                            )}
                          </ListItemIcon>
                          <ListItemText
                            primary={<Typography variant="caption">At least 12 characters</Typography>}
                          />
                        </ListItem>
                        <ListItem sx={{ py: 0 }}>
                          <ListItemIcon sx={{ minWidth: 32 }}>
                            {/[A-Z]/.test(password) ? (
                              <CheckCircleIcon sx={{ color: 'success.main', fontSize: 18 }} />
                            ) : (
                              <CancelIcon sx={{ color: 'error.main', fontSize: 18 }} />
                            )}
                          </ListItemIcon>
                          <ListItemText
                            primary={<Typography variant="caption">One uppercase letter</Typography>}
                          />
                        </ListItem>
                        <ListItem sx={{ py: 0 }}>
                          <ListItemIcon sx={{ minWidth: 32 }}>
                            {/[a-z]/.test(password) ? (
                              <CheckCircleIcon sx={{ color: 'success.main', fontSize: 18 }} />
                            ) : (
                              <CancelIcon sx={{ color: 'error.main', fontSize: 18 }} />
                            )}
                          </ListItemIcon>
                          <ListItemText
                            primary={<Typography variant="caption">One lowercase letter</Typography>}
                          />
                        </ListItem>
                        <ListItem sx={{ py: 0 }}>
                          <ListItemIcon sx={{ minWidth: 32 }}>
                            {/[0-9]/.test(password) ? (
                              <CheckCircleIcon sx={{ color: 'success.main', fontSize: 18 }} />
                            ) : (
                              <CancelIcon sx={{ color: 'error.main', fontSize: 18 }} />
                            )}
                          </ListItemIcon>
                          <ListItemText
                            primary={<Typography variant="caption">One number</Typography>}
                          />
                        </ListItem>
                        <ListItem sx={{ py: 0 }}>
                          <ListItemIcon sx={{ minWidth: 32 }}>
                            {/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password) ? (
                              <CheckCircleIcon sx={{ color: 'success.main', fontSize: 18 }} />
                            ) : (
                              <CancelIcon sx={{ color: 'error.main', fontSize: 18 }} />
                            )}
                          </ListItemIcon>
                          <ListItemText
                            primary={<Typography variant="caption">One special character (!@#$%^&* etc.)</Typography>}
                          />
                        </ListItem>
                      </List>

                      {/* Show warnings */}
                      {passwordValidation.warnings.length > 0 && (
                        <Box sx={{ mt: 1 }}>
                          {passwordValidation.warnings.map((warning, idx) => (
                            <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                              <WarningIcon sx={{ color: 'warning.main', fontSize: 16 }} />
                              <Typography variant="caption" color="warning.dark">
                                {warning}
                              </Typography>
                            </Box>
                          ))}
                        </Box>
                      )}
                    </Paper>
                  )}
                </Box>
              )}

              {/* Birth Date & Time */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                  Birth Date & Time
                </Typography>
                <DatePicker
                  selected={birthDate}
                  onChange={(date) => date && setBirthDate(date)}
                  showTimeSelect
                  timeFormat="HH:mm"
                  timeIntervals={15}
                  dateFormat="MMMM d, yyyy h:mm aa"
                  minDate={new Date('1900-01-01')}
                  maxDate={new Date()}
                  showYearDropdown
                  showMonthDropdown
                  dropdownMode="select"
                  customInput={<TextField fullWidth />}
                />
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                  Exact birth time is required for accurate house calculations
                </Typography>
              </Box>

              {/* Birth Location */}
              <TextField
                fullWidth
                label="Birth Location"
                value={cityName}
                onChange={(e) => handleCityChange(e.target.value)}
                onBlur={(e) => handleCityChange(e.target.value)}
                placeholder="City, State/Country"
                required
                sx={{ mb: 3 }}
                helperText="Start typing to see suggestions"
                inputProps={{
                  list: "city-suggestions-register"
                }}
              />
              <datalist id="city-suggestions-register">
                {getCityList().map(city => (
                  <option key={city} value={city} />
                ))}
              </datalist>

              {/* Show coordinates if location selected */}
              {latitude !== 0 && (
                <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Location:</strong> {cityName}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Coordinates:</strong> {latitude.toFixed(4)}°, {longitude.toFixed(4)}°
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Timezone:</strong> {timezone}
                  </Typography>
                </Box>
              )}

              {/* Submit Button */}
              <Button
                type="submit"
                variant="contained"
                fullWidth
                size="large"
                disabled={loading}
                sx={{
                  py: { xs: 1.5, sm: 2 },
                  fontSize: { xs: '0.875rem', sm: '1rem' },
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)',
                  }
                }}
              >
                {loading ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CircularProgress size={20} sx={{ color: 'white' }} />
                    <span>Creating account...</span>
                  </Box>
                ) : (
                  'Create Account'
                )}
              </Button>
            </form>

            {/* Login Link */}
            <Box sx={{ mt: 3, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                Already have an account?{' '}
                <Button
                  onClick={() => navigate('/transit-calendar/login')}
                  sx={{ textTransform: 'none', fontWeight: 600 }}
                >
                  Log in
                </Button>
              </Typography>
            </Box>
          </Paper>
        </motion.div>
      </Container>
    </Box>
  );
};

export default TransitCalendarRegister;
